<template>
  <tr
    class="bg-white hover:bg-gray-50 transition-colors duration-150 ease-in-out text-right"
    @click="open = !open"
  >
    <td class="flex pl-3 py-5 items-center space-x-1">
      <button
        type="button"
        class="btn-icon btn-icon-default"
        data-test="open-dayworks"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-all duration-200"
          :class="[{'rotate-90': open}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
      </button>

      <span>Dayworks</span>
    </td>

    <td class="pr-3">
      {{ australianCurrency(totalCosts.calculated) }}
    </td>

    <td v-if="claim.boq_type === 'subcontractor_schedule_of_rates'" />

    <td
      v-if="claim.capture_claim_amount"
      class="bg-green-200/50"
    >
      {{ australianCurrency(totalCosts.currentClaim) }}
    </td>

    <td class="bg-green-200/50">
      {{ australianCurrency(totalCosts.currentCertified) }}
    </td>

    <td
      v-if="claim.capture_claim_amount"
      class="bg-green-200/50"
      :class="{ 'text-red-600': totalCosts.variance !== 0 }"
    >
      {{ australianCurrency(totalCosts.variance) }}
    </td>

    <td class="bg-green-200/50">
      {{ australianCurrency(totalCosts.previous) }}
    </td>

    <td class="bg-green-200/50 pr-3">
      {{ australianCurrency(totalCosts.cumulative) }}
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
      colspan="3"
    />

    <td class="bg-blue-100/50 pr-3" />

    <td />

    <td class="bg-orange-200/30 relative z-10" />

    <td
      v-if="showForecasts"
      class="bg-orange-200/30 relative z-10 pr-3"
    />
  </tr>

  <template v-if="open">
    <tr
      v-if="labours.length"
      class="bg-gray-100 text-left"
    >
      <td
        class="pl-10 py-2 font-semibold text-sm text-gray-700"
        colspan="100%"
      >
        Labour
      </td>
    </tr>

    <DayworksCostRow
      v-for="labour in labours"
      :key="`labour-${labour.id}`"
      :type="DayworksType.labour"
      :labour="labour"
      :claim="claim"
      :can-edit-certified="canEditCertified"
      :lite-mode="liteMode"
      :project-id="projectId"
      :show-percentages="showPercentages"
      :show-forecasts="showForecasts"
    />

    <tr
      v-if="plants.length"
      class="bg-gray-100 text-left"
    >
      <td
        class="pl-10 py-2 font-semibold text-sm text-gray-700"
        colspan="100%"
      >
        Plant
      </td>
    </tr>

    <DayworksCostRow
      v-for="plant in plants"
      :key="`plant-${plant.id}`"
      :type="DayworksType.plant"
      :plant="plant"
      :claim="claim"
      :can-edit-certified="canEditCertified"
      :lite-mode="liteMode"
      :project-id="projectId"
      :show-percentages="showPercentages"
      :show-forecasts="showForecasts"
    />
  </template>
</template>

<script setup lang="ts">
import { australianCurrency } from '@/helpers';
import Claim from '@/models/Claim';
import { DayworksType } from '@/models/DayworksCost';
import { useLaboursStore } from '@/store/labours';
import { usePlantsStore } from '@/store/plants';
import { computed, ref } from 'vue';
import DayworksCostRow from './DayworksCostRow.vue';

const props = defineProps<{
  claim: Claim;
  canEditCertified: boolean;
  liteMode?: boolean;
  projectId?: string;
  showPercentages?: boolean;
  showForecasts?: boolean;
}>();

const open = ref(false);
const laboursStore = useLaboursStore();
const plantsStore = usePlantsStore();

const labours = computed(() => {
  return laboursStore.models
    .where('$claim_id', props.claim.id)
    .orderBy('first_name')
    .orderBy('last_name')
    .with('dayworks_costs', (query) => {
      query.where('claim_id', props.claim.id);
    })
    .get();
});

const plants = computed(() => {
  return plantsStore.models
    .where('$claim_id', props.claim.id)
    .orderBy('fleet')
    .with('dayworks_costs', (query) => {
      query.where('claim_id', props.claim.id);
    })
    .get();
});

const totalCosts = computed(() => {
  const totals = {
    calculated: 0,
    currentClaim: 0,
    currentCertified: 0,
    previous: 0,
    cumulative: 0,
    variance: 0,
  };

  labours.value.forEach((labour) => {
    const cost = labour.dayworks_costs[0];

    totals.calculated += labour.calculatedAmount || 0;
    totals.currentClaim += cost?.current_claim || 0;
    totals.currentCertified += cost?.current_certified || 0;
    totals.previous += labour.$previous || 0;
    totals.variance += (cost?.current_claim || 0) - (cost?.current_certified || 0);
  });

  plants.value.forEach((plant) => {
    const cost = plant.dayworks_costs[0];

    totals.calculated += plant.calculatedAmount || 0;
    totals.currentClaim += cost?.current_claim || 0;
    totals.currentCertified += cost?.current_certified || 0;
    totals.previous += plant.$previous || 0;
    totals.variance += (cost?.current_claim || 0) - (cost?.current_certified || 0);
  });

  totals.cumulative = totals.previous + totals.currentCertified;

  return totals;
});
</script>
